import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, RootState } from "../../store";
import { ProcessingContainer } from "./styled";
import WalletForm from "../../components/WalletForm";
import InfoBlock from "./InfoBlock";
import FormSteps from "../../components/FormSteps";
import { Divider } from "../../components/styled/Divider";
import { Ripple } from "../../components/Loaders/Ripple";
import { SnapshotStatus } from "../../types";
import ErrorBlock from "./ErrorBlock";
import { Row } from "../../components/styled/Flex";
import Button from "../../components/styled/Button";
import useSnapshotHistory from "../../hooks/useSnapshotHistory";
import { useWeb3React } from "@web3-react/core";
import { SNAPSHOT_CHECK_INTERVAL } from "../../config/constants";

const Processing = () => {
  const { snapshotHistory, storeSnapshotHistory } = useSnapshotHistory();
  const { account, chainId } = useWeb3React();
  const dispatch = useDispatch<Dispatch>();
  const [isProcessing, setIsProcessing] = useState(true);
  const [isError, setIsError] = useState(false);
  const currentSnapshotId = useSelector(
    (state: RootState) => state.snapshotModel.currentSnapshotId
  );

  const snapshotData = useSelector(
    (state: RootState) => state.snapshotModel.snapshotData
  );

  const apiSnapshotStatus = useSelector(
    (state: RootState) => state.snapshotModel.snapshotStatus
  );

  const navigator = useNavigate();

  useEffect(() => {
    if (!currentSnapshotId || !account || !chainId) return;
    const snapshot = snapshotHistory.find(
      (snapshot) => snapshot.snapshotId === currentSnapshotId
    );
    if (!snapshot) {
      storeSnapshotHistory(account, currentSnapshotId, chainId);
    }
  }, [
    currentSnapshotId,
    snapshotHistory,
    account,
    chainId,
    storeSnapshotHistory,
  ]);

  useEffect(() => {
    const checkSnapshotStatus = async () => {
      if (!currentSnapshotId || !account || !chainId) return;
      await dispatch.snapshotModel.checkSnapshotStatus(currentSnapshotId);
      setIsProcessing(true);

      if (snapshotData && snapshotData?.length > 0) {
        setIsProcessing(false);
        clearInterval(interval);
      }
    };

    checkSnapshotStatus();

    const interval = setInterval(
      async () => checkSnapshotStatus(),
      SNAPSHOT_CHECK_INTERVAL
    );

    if (!isProcessing || isError) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [
    currentSnapshotId,
    account,
    chainId,
    dispatch.snapshotModel,
    storeSnapshotHistory,
    snapshotHistory,
    snapshotData,
    isError,
    isProcessing,
  ]);

  useEffect(() => {
    if (!apiSnapshotStatus) return;
    if (apiSnapshotStatus === SnapshotStatus.COMPLETED) {
      navigator("/snapshot-review");
    }

    if (apiSnapshotStatus === SnapshotStatus.ERROR) {
      setIsError(true);
      setIsProcessing(false);
    }

    if (apiSnapshotStatus === SnapshotStatus.NOT_FOUND) {
      navigator("/token-submit");
      dispatch.snapshotModel.resetState();
    }
  }, [apiSnapshotStatus, navigator, dispatch.snapshotModel]);

  useEffect(() => {
    if (!currentSnapshotId) return navigator("/token-submit");
    dispatch.multistepModel.setCurrentStep(2);
    return () => {
      dispatch.multistepModel.setCurrentStep(1);
    };
  }, [dispatch, currentSnapshotId, navigator]);

  const restartSnapshot = () => {
    navigator("/token-submit");
    dispatch.snapshotModel.resetState();
  };

  return (
    <ProcessingContainer>
      <WalletForm marginTop="50px" marginBottom="50px">
        <FormSteps />
        <Divider />

        {isProcessing && currentSnapshotId && !isError ? (
          <InfoBlock snapshotId={currentSnapshotId} />
        ) : null}

        {isError && currentSnapshotId && (
          <>
            <ErrorBlock />
            <Row
              style={{
                marginTop: "50px",
              }}
            >
              <Button onClick={restartSnapshot}>Try Again</Button>
            </Row>
          </>
        )}

        {!isError && isProcessing && (
          <Row
            style={{
              marginTop: "50px",
            }}
          >
            <Ripple />
          </Row>
        )}

        {!isProcessing && isError && (
          <Row
            style={{
              marginTop: "50px",
            }}
          >
            <Button onClick={restartSnapshot}>Try Again</Button>
          </Row>
        )}
      </WalletForm>
    </ProcessingContainer>
  );
};

export default Processing;
